import { Box, Button, Typography, useMediaQuery } from "@mui/material";
import { useTheme, styled } from "@mui/material/styles";
import { RenderHTML } from "components";
import {
  CourseCompletionIconLanding,
  CourseRequiredIconLanding,
  RequiredLearningIconLanding,
} from "components/common-icons/common-icons";
import { useFocusStyles } from "hooks/focusBorder";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "store";
import {
  CERTIFICATIONS_ACQUIRED,
  COURSES_COMPLETE,
  REQUIRED_LEARNING,
  SORT_DEFAULT,
} from "utils/constants";
import portalSettingsManager from "utils/portalSettingsManager";
import { CategoryCode } from "utils/types";

const MatrixBox = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  marginBottom: "20px",
  cursor: "pointer",
  flexShrink: 0,
  [theme.breakpoints.down("sm")]: {
    display: "block",
  },
}));

const MessageTextBox = styled(Box)(({ theme }) => ({
  "& p": { textAlign: "left !important", fontSize: "14px", fontFamily: "Arial" },
  "& a": {
    textDecoration: "underline",
    color: portalSettingsManager?.landingPageSubHeader?.colors?.welcomeText?.text + " !important",
  },
  "& table": {
    width: "100%",
    [theme.breakpoints.down("md")]: {
      width: "90% !important",
    },
    [theme.breakpoints.down("sm")]: {
      width: "75% !important",
    },
  },
  "& tr": {
    [theme.breakpoints.down("md")]: {
      display: "block",
      marginBottom: ".6rem",
    },
  },
  "& td": {
    [theme.breakpoints.down("md")]: {
      display: "block",
      borderBottom: "none",
      width: "100%",
    },
  },
  "& video": {
    [theme.breakpoints.down("md")]: {
      width: "100%",
    },
    [theme.breakpoints.down("sm")]: {
      width: "135%",
    },
  },
}));

const Matrix = ({
  bg,
  label,
  count,
  handleMetricSearch,
  metricsType,
  labelColor,
  iconBackground,
  metricsColor,
}: any): React.ReactElement => {
  return (
    <MatrixBox onClick={() => handleMetricSearch(metricsType)} tabIndex={0}>
      <Box
        style={{
          backgroundColor: iconBackground,
          height: "48px",
          width: "52px",
          minWidth: "52px",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ cursor: "pointer", height: "27px" }}>{bg}</div>
      </Box>
      <Box style={{ display: "flex", flexDirection: "column", marginLeft: "10px" }}>
        <Typography
          style={{
            fontSize: "24px",
            fontWeight: "bold",
            lineHeight: "31px",
            textAlign: "start",
            color: metricsColor,
          }}
        >
          {count}
        </Typography>
        <Typography
          style={{
            fontSize: "11px",
            lineHeight: "16px",
            textAlign: "start",
            minWidth: "75px",
            maxWidth: "75px",
            color: labelColor,
          }}
        >
          {label}
        </Typography>
      </Box>
    </MatrixBox>
  );
};

export const Welcome = ({
  name,
  message,
  learningCount,
  completionCount,
  isLandingPage,
  certificationCount,
  metricsList,
}: any): React.ReactElement => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const focusClass = useFocusStyles();
  const [isExpanded, setIsExpanded] = useState(
    portalSettingsManager.application?.common?.isWelcomeMessagePersistent,
  );

  function toggleIsExpanded() {
    setIsExpanded(!isExpanded);
  }

  const isGreetByName = portalSettingsManager.application?.common?.greetLearnerByName;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMaxWidth = useMediaQuery("(max-width:215px)");
  const haldeMetricSearch = (type: string) => {
    dispatch({
      type: "metrics/filter",
      payload: type,
    });
    if (type === "Required") {
      dispatch({
        type: "subheader/title",
        payload: REQUIRED_LEARNING,
      });
    } else if (type === "Courses") {
      dispatch({
        type: "subheader/title",
        payload: COURSES_COMPLETE,
      });
    } else if (type === "Certifications") {
      dispatch({
        type: "subheader/title",
        payload: CERTIFICATIONS_ACQUIRED,
      });
    }
    dispatch({
      type: "search/setSearchFilters",
      payload: {
        sort: SORT_DEFAULT,
        text: "",
        code: isLandingPage ? CategoryCode.Required : CategoryCode.All,
      },
    });

    navigate("/catalog/MyLearning");
  };

  message =
    message && message !== null && message !== ""
      ? message
          .replace(/^"(.*)"$/, "$1")
          .replace(/\\n/g, "")
          .replace(/\/n/g, "")
          .replace(/\\/g, "")
          .replace(
            /controls/g,
            'controls disablepictureinpicture muted controlsList="nodownload noremoteplayback noplaybackrate"',
          )
      : "";

  return (
    <Box
      p={isMobile ? "25px 60px 25px 25px" : "25px 60px 25px 70px"}
      borderBottom="1px solid #c9c9c9"
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        justifyContent: "space-around",
        color: portalSettingsManager?.landingPageSubHeader?.colors?.base?.text,
        backgroundColor: portalSettingsManager?.landingPageSubHeader?.colors?.base?.back,
      }}
    >
      <Box style={{ marginRight: "40px" }}>
        <Typography
          component="h1"
          style={{
            fontSize: "22px",
            marginBottom: "6px",
            color: portalSettingsManager?.landingPageSubHeader?.colors?.base?.text,
          }}
        >
          {t("welcome_text")}
          <strong> {isGreetByName ? name : ""}</strong>
          {message &&
            message !== null &&
            message !== "" &&
            portalSettingsManager.application.features.showWelcomeTextButton && (
              <Button
                style={{
                  marginLeft: "20px",
                  borderRadius: 0,
                  padding: "10px 14px",
                  height: "36px",
                  color: portalSettingsManager?.buttonColors?.showHideWelcomeText?.active?.text,
                  border: `1px solid ${portalSettingsManager?.buttonColors?.showHideWelcomeText?.active?.border}`,
                  fontSize: "12px",
                  backgroundColor:
                    portalSettingsManager?.buttonColors?.showHideWelcomeText?.active?.back ||
                    theme.palette.primary.main,
                }}
                aria-expanded={isExpanded}
                className={`${focusClass.primaryHover} ${focusClass.focusItem}`}
                onClick={toggleIsExpanded}
              >
                {isExpanded ? t("close_message") : t("view_message")}
              </Button>
            )}
        </Typography>
        {isExpanded && message && (
          <Box
            style={{
              display: "flex",
              width: isMaxWidth ? "206%" : "115%",
              lineHeight: "21px",
              backgroundColor:
                portalSettingsManager?.landingPageSubHeader?.colors?.welcomeText?.back,
              color: portalSettingsManager?.landingPageSubHeader?.colors?.welcomeText?.text,
            }}
            textAlign={{ xs: "center", sm: "left" }}
          >
            <MessageTextBox>
              <RenderHTML content={message} />
            </MessageTextBox>
          </Box>
        )}
      </Box>
      {metricsList &&
        ((learningCount !== undefined && learningCount !== 0) ||
          (completionCount !== undefined && completionCount !== 0) ||
          (certificationCount !== undefined && certificationCount !== 0)) && (
          <Box>
            <Typography
              style={{
                fontSize: "1em",
                color: portalSettingsManager?.landingPageSubHeader?.colors?.base?.text,
              }}
            >
              {t("my_progress")}
            </Typography>
            <Box
              style={{ display: "flex", marginTop: "16px" }}
              role="link"
              flexDirection={isMobile || !isExpanded ? "row" : "column"}
            >
              {learningCount !== undefined && learningCount !== 0 && (
                <Matrix
                  bg={
                    <RequiredLearningIconLanding
                      htmlColor={
                        portalSettingsManager?.landingPageSubHeader?.colors?.requiredIcon?.text
                      }
                      height="100%"
                      width="100%"
                    />
                  }
                  label={t("required_learning_metric")}
                  className={focusClass.focusItem}
                  count={learningCount}
                  handleMetricSearch={haldeMetricSearch}
                  inputProps={{ tabIndex: 0 }}
                  metricsType="Required"
                  role="link"
                  labelColor={
                    portalSettingsManager?.landingPageSubHeader?.colors?.metricsNumbers?.required
                  }
                  iconBackground={
                    portalSettingsManager?.landingPageSubHeader?.colors?.requiredIcon?.back
                  }
                  metricsColor={
                    portalSettingsManager?.landingPageSubHeader?.colors?.metricsNumbers?.required
                  }
                />
              )}
              {completionCount !== undefined && completionCount !== 0 && (
                <Matrix
                  bg={
                    <CourseCompletionIconLanding
                      htmlColor={
                        portalSettingsManager?.landingPageSubHeader?.colors?.completeIcon?.text
                      }
                      height="100%"
                      width="100%"
                    />
                  }
                  label={t("courses_complete_metric")}
                  count={completionCount}
                  className={focusClass.borderItem}
                  handleMetricSearch={haldeMetricSearch}
                  metricsType="Courses"
                  labelColor={
                    portalSettingsManager?.landingPageSubHeader?.colors?.metricsNumbers?.complete
                  }
                  iconBackground={
                    portalSettingsManager?.landingPageSubHeader?.colors?.completeIcon?.back
                  }
                  metricsColor={
                    portalSettingsManager?.landingPageSubHeader?.colors?.metricsNumbers?.complete
                  }
                />
              )}
              {certificationCount !== undefined && certificationCount !== 0 && (
                <Matrix
                  bg={
                    <CourseRequiredIconLanding
                      htmlColor={
                        portalSettingsManager?.landingPageSubHeader?.colors?.certificateIcon?.text
                      }
                      height="100%"
                      width="100%"
                    />
                  }
                  label={t("certifications_acquired_metric")}
                  className={focusClass.focusItem}
                  count={certificationCount}
                  handleMetricSearch={haldeMetricSearch}
                  metricsType="Certifications"
                  labelColor={
                    portalSettingsManager?.landingPageSubHeader?.colors?.metricsNumbers?.certificate
                  }
                  iconBackground={
                    portalSettingsManager?.landingPageSubHeader?.colors?.certificateIcon?.back
                  }
                  metricsColor={
                    portalSettingsManager?.landingPageSubHeader?.colors?.metricsNumbers?.certificate
                  }
                />
              )}
            </Box>
          </Box>
        )}
    </Box>
  );
};
